import React, { useEffect, useState } from 'react';
import api from '../api/axiosConfig';
import { apiErrorText } from '../utils/apiErrors';
import { useAuth } from '../context/AuthContext';
import './UsersPage.css';
import '../styles/rowActions.css';

const BLANK_FORM = {
  username: '', email: '', first_name: '', last_name: '', password: '',
  is_staff: false, is_superuser: false, company: '',
};

const NAV_SECTIONS = [
  { key: 'production', label: 'Producción · Control de Enfunde' },
  { key: 'harvest', label: 'Producción · Detalles de Cosecha' },
  { key: 'recobro', label: 'Producción · Recobro de Cintas' },
  { key: 'production_summary', label: 'Producción · Resumen de Producción' },
  { key: 'production_budget', label: 'Producción · Presupuesto de Cajas' },
  { key: 'fumigation', label: 'Producción · Fumigación Aérea' },
  { key: 'ventas', label: 'Ventas' },
  { key: 'sales_summary', label: 'Ventas · Resumen de Ventas', superuserOnly: true },
  { key: 'despachos', label: 'Despachos' },
  { key: 'admin_settings', label: 'Admin · Ajustes' },
  { key: 'admin_companies', label: 'Admin · Empresas' },
  { key: 'admin_farms', label: 'Admin · Fincas' },
  { key: 'admin_bunch_sleevers', label: 'Admin · Enfundadores' },
  { key: 'admin_ribbons', label: 'Admin · Cintas' },
  { key: 'admin_exporters', label: 'Admin · Exportadores' },
  { key: 'admin_brands', label: 'Admin · Marcas' },
  { key: 'admin_transportations', label: 'Admin · Transportes' },
  { key: 'admin_destinations', label: 'Admin · Destinos' },
  { key: 'admin_drivers', label: 'Admin · Conductores' },
  { key: 'admin_box_types', label: 'Admin · Tipos de Caja' },
  { key: 'admin_inspectors', label: 'Admin · Inspectores' },
  { key: 'admin_fumigation_products', label: 'Admin · Productos de Fumigación' },
  { key: 'admin_fumigation_companies', label: 'Admin · Empresas de Fumigación' },
  { key: 'admin_fumigation_pilots', label: 'Admin · Pilotos de Fumigación' },
  { key: 'admin_users', label: 'Admin · Usuarios' },
  { key: 'novedades', label: 'Novedades' },
];

const EXTRA_PERMISSIONS = [
  { key: 'can_reopen_weeks', label: 'Puede reabrir semanas cerradas' },
  { key: 'sale_prices', label: 'Puede ver y asignar precios de ventas' },
  { key: 'delete_records', label: 'Puede eliminar registros en los historiales' },
  { key: 'delete_lotes', label: 'Puede eliminar lotes en Control de Enfunde' },
];

const ACTIVITY_PERMISSION = {
  key: 'view_user_activity',
  label: 'Puede ver la página y el tiempo activo de otros usuarios',
};

const SUPERUSER_EXTRA_PERMISSIONS = [...EXTRA_PERMISSIONS, ACTIVITY_PERMISSION];

// Options only superusers see are tinted (users-superuser-only) and say so on hover.
const SUPERUSER_ONLY_TITLE = 'Solo los superusuarios ven esta opción';

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const isSuperuser = !!currentUser?.is_superuser;
  const isStaffOrSuper = !!(currentUser?.is_staff || currentUser?.is_superuser);

  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  const [form, setForm] = useState(BLANK_FORM);
  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(BLANK_FORM);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [permsOpenId, setPermsOpenId] = useState(null);
  const [permsDraft, setPermsDraft] = useState({});
  const [savingPerms, setSavingPerms] = useState(false);

  const loadUsers = () => {
    setLoading(true);
    return api.get('users/')
      .then((r) => setUsers(r.data))
      .catch(() => setStatus({ type: 'error', text: 'No se pudieron cargar los usuarios.' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadUsers();
    api.get('companies/').then((r) => setCompanies(r.data)).catch(() => {});
  }, []);

  const handleFormChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || !form.password.trim()) return;
    setCreating(true);
    setStatus(null);
    try {
      const payload = { ...form, company: form.company ? Number(form.company) : null };
      const r = await api.post('users/', payload);
      setUsers((us) => [...us, r.data].sort((a, b) => a.username.localeCompare(b.username)));
      setForm(BLANK_FORM);
    } catch (err) {
      setStatus({ type: 'error', text: apiErrorText(err, 'No se pudo crear el usuario.') });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setEditForm({ ...BLANK_FORM, ...u, password: '', company: u.company != null ? String(u.company) : '' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(BLANK_FORM);
  };

  const handleEditChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setEditForm((f) => ({ ...f, [field]: value }));
  };

  const handleSaveEdit = async (id) => {
    setSavingId(id);
    setStatus(null);
    try {
      const payload = { ...editForm, company: editForm.company ? Number(editForm.company) : null };
      if (!payload.password) delete payload.password;
      delete payload.nav_permissions;
      const r = await api.patch(`users/${id}/`, payload);
      setUsers((us) => us.map((u) => (u.id === id ? r.data : u)));
      cancelEdit();
    } catch (err) {
      setStatus({ type: 'error', text: apiErrorText(err, 'No se pudieron guardar los cambios.') });
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (u) => {
    if (u.id === currentUser.id) return;
    if (!window.confirm(`¿Eliminar al usuario "${u.username}"?`)) return;
    setDeletingId(u.id);
    setStatus(null);
    try {
      await api.delete(`users/${u.id}/`);
      setUsers((us) => us.filter((x) => x.id !== u.id));
    } catch (err) {
      setStatus({ type: 'error', text: 'No se pudo eliminar el usuario.' });
    } finally {
      setDeletingId(null);
    }
  };

  const startEditPerms = (u) => {
    setPermsOpenId(u.id);
    setPermsDraft(u.nav_permissions || {});
  };

  const cancelEditPerms = () => {
    setPermsOpenId(null);
    setPermsDraft({});
  };

  const togglePerm = (key) => {
    setPermsDraft((d) => ({ ...d, [key]: !d[key] }));
  };

  const handleSavePerms = async (id) => {
    setSavingPerms(true);
    setStatus(null);
    try {
      const r = await api.patch(`users/${id}/`, { nav_permissions: permsDraft });
      setUsers((us) => us.map((u) => (u.id === id ? r.data : u)));
      cancelEditPerms();
    } catch (err) {
      setStatus({ type: 'error', text: 'No se pudieron guardar los permisos.' });
    } finally {
      setSavingPerms(false);
    }
  };

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    const fullName = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
    return (u.username || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      fullName.includes(q);
  });

  return (
    <div className="users-root">
      <h1 className="users-title">👥 Usuarios</h1>

      <form className="users-add-form" onSubmit={handleCreate}>
        <input className="users-input" placeholder="Usuario" value={form.username} onChange={handleFormChange('username')} required />
        <input className="users-input" type="email" placeholder="Correo" value={form.email} onChange={handleFormChange('email')} />
        <input className="users-input" placeholder="Nombre" value={form.first_name} onChange={handleFormChange('first_name')} />
        <input className="users-input" placeholder="Apellido" value={form.last_name} onChange={handleFormChange('last_name')} />
        <input className="users-input" type="password" placeholder="Contraseña" value={form.password} onChange={handleFormChange('password')} required />
        <select className="users-input" value={form.company} onChange={handleFormChange('company')}>
          <option value="">Todas las empresas</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        {(isStaffOrSuper || isSuperuser) && (
          <div className="users-role-toggles">
            {isStaffOrSuper && (
              <label className="users-checkbox">
                <input type="checkbox" checked={form.is_staff} onChange={handleFormChange('is_staff')} /> Staff
              </label>
            )}
            {isSuperuser && (
              <label className="users-checkbox users-superuser-only" title={SUPERUSER_ONLY_TITLE}>
                <input type="checkbox" checked={form.is_superuser} onChange={handleFormChange('is_superuser')} /> Superusuario
              </label>
            )}
          </div>
        )}
        <button className="users-add-btn" type="submit" disabled={creating}>
          {creating ? 'Agregando…' : '+ Agregar usuario'}
        </button>
      </form>

      {status && <div className={`users-status users-status-${status.type}`}>{status.text}</div>}

      <input
        className="users-search"
        placeholder="Buscar por usuario, correo o nombre…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="users-card">
        {loading ? (
          <div className="users-empty">Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="users-empty">No se encontraron usuarios.</div>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Correo</th>
                <th>Nombre</th>
                <th>Rol</th>
                <th>Empresa</th>
                <th>Ingresó</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const isFullAccess = u.is_staff || u.is_superuser;
                const canOpenPermissions = !u.is_superuser && (!isFullAccess || isSuperuser);
                // Superuser accounts can only be changed by another superuser (enforced by the API too).
                const isProtected = u.is_superuser && !isSuperuser;
                return (
                <React.Fragment key={u.id}>
                <tr>
                  {editingId === u.id ? (
                    <>
                      <td>{u.username}</td>
                      <td><input className="users-edit-input" value={editForm.email} onChange={handleEditChange('email')} /></td>
                      <td>
                        <input className="users-edit-input" style={{ marginBottom: 4 }} value={editForm.first_name} onChange={handleEditChange('first_name')} placeholder="Nombre" />
                        <input className="users-edit-input" value={editForm.last_name} onChange={handleEditChange('last_name')} placeholder="Apellido" />
                      </td>
                      <td>
                        {isStaffOrSuper && (
                          <label className="users-checkbox">
                            <input type="checkbox" checked={editForm.is_staff} onChange={handleEditChange('is_staff')} /> Staff
                          </label>
                        )}
                        {isSuperuser && (
                          <label className="users-checkbox users-superuser-only" title={SUPERUSER_ONLY_TITLE}>
                            <input type="checkbox" checked={editForm.is_superuser} onChange={handleEditChange('is_superuser')} /> Super
                          </label>
                        )}
                        <input
                          className="users-edit-input"
                          type="password"
                          placeholder="Nueva contraseña (opcional)"
                          value={editForm.password}
                          onChange={handleEditChange('password')}
                        />
                      </td>
                      <td>
                        <select className="users-edit-input" value={editForm.company} onChange={handleEditChange('company')}>
                          <option value="">Todas las empresas</option>
                          {companies.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </td>
                      <td>{new Date(u.date_joined).toLocaleDateString()}</td>
                      <td>
                        <div className="row-actions">
                          <button
                            className="row-icon-btn"
                            onClick={() => handleSaveEdit(u.id)}
                            disabled={savingId === u.id}
                            title="Guardar"
                            aria-label="Guardar"
                          >
                            {savingId === u.id ? '…' : '💾'}
                          </button>
                          <button className="row-icon-btn" onClick={cancelEdit} title="Cancelar" aria-label="Cancelar">✖️</button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>
                        {u.username}
                        {u.id === currentUser.id && <span className="users-you-badge">Tú</span>}
                      </td>
                      <td>{u.email || '—'}</td>
                      <td>{[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}</td>
                      <td>
                        <span className="users-role-badge">
                          {u.is_superuser ? 'Superusuario' : u.is_staff ? 'Staff' : 'Miembro'}
                        </span>
                      </td>
                      <td>{isFullAccess ? 'Todas' : (u.company_name || 'Todas')}</td>
                      <td>{new Date(u.date_joined).toLocaleDateString()}</td>
                      <td>
                        <div className="row-actions">
                          {!isProtected && (
                            <button className="row-icon-btn" onClick={() => startEdit(u)} title="Editar" aria-label="Editar">✏️</button>
                          )}
                          {canOpenPermissions && (
                            <button
                              className="row-icon-btn"
                              onClick={() => startEditPerms(u)}
                              title="Permisos"
                              aria-label="Permisos"
                            >
                              🔑
                            </button>
                          )}
                          {u.id !== currentUser.id && !isProtected && (
                            <button
                              className="row-icon-btn"
                              onClick={() => handleDelete(u)}
                              disabled={deletingId === u.id}
                              title="Eliminar"
                              aria-label="Eliminar"
                            >
                              {deletingId === u.id ? '…' : '🗑️'}
                            </button>
                          )}
                        </div>
                      </td>
                    </>
                  )}
                </tr>
                {permsOpenId === u.id && (
                  <tr className="users-perms-row">
                    <td colSpan={7}>
                      <div className="users-perms-panel">
                        <div className="users-perms-title">Permisos de {u.username}</div>
                        {u.is_staff ? (
                          <>
                            <div className="users-perms-title">Permiso especial</div>
                            <div className="users-perms-grid">
                              {isSuperuser && [ACTIVITY_PERMISSION].map((s) => (
                                <label key={s.key} className="users-checkbox users-superuser-only" title={SUPERUSER_ONLY_TITLE}>
                                  <input
                                    type="checkbox"
                                    checked={!!permsDraft[s.key]}
                                    onChange={() => togglePerm(s.key)}
                                  />
                                  {s.label}
                                </label>
                              ))}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="users-perms-title">Secciones visibles</div>
                            <div className="users-perms-grid">
                              {NAV_SECTIONS.filter((s) => !s.superuserOnly || isSuperuser).map((s) => (
                                <label
                                  key={s.key}
                                  className={`users-checkbox${s.superuserOnly ? ' users-superuser-only' : ''}`}
                                  title={s.superuserOnly ? SUPERUSER_ONLY_TITLE : undefined}
                                >
                                  <input
                                    type="checkbox"
                                    checked={!!permsDraft[s.key]}
                                    onChange={() => togglePerm(s.key)}
                                  />
                                  {s.label}
                                </label>
                              ))}
                            </div>
                            <div className="users-perms-divider" />
                            <div className="users-perms-title">Otros permisos</div>
                            <div className="users-perms-grid">
                              {(isSuperuser ? SUPERUSER_EXTRA_PERMISSIONS : EXTRA_PERMISSIONS).map((s) => (
                                <label
                                  key={s.key}
                                  className={`users-checkbox${s.key === ACTIVITY_PERMISSION.key ? ' users-superuser-only' : ''}`}
                                  title={s.key === ACTIVITY_PERMISSION.key ? SUPERUSER_ONLY_TITLE : undefined}
                                >
                                  <input
                                    type="checkbox"
                                    checked={!!permsDraft[s.key]}
                                    onChange={() => togglePerm(s.key)}
                                  />
                                  {s.label}
                                </label>
                              ))}
                            </div>
                          </>
                        )}
                        <div className="users-perms-divider" />
                        <div className="row-actions users-perms-actions">
                          <button
                            className="row-icon-btn"
                            onClick={() => handleSavePerms(u.id)}
                            disabled={savingPerms}
                            title="Guardar permisos"
                            aria-label="Guardar permisos"
                          >
                            {savingPerms ? '…' : '💾'}
                          </button>
                          <button
                            className="row-icon-btn"
                            onClick={cancelEditPerms}
                            title="Cancelar"
                            aria-label="Cancelar"
                          >
                            ✖️
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
